<script lang="ts" setup>
import request from '~/api/utils';

enum FormVariant {
  phone = 'phone',
  phoneCode = 'phone-code',
  email = 'email',
  forgotPassword = 'forgot-password',
  emailSent = 'email-sent',
}

const props = defineProps<{
  title?: string;
  titleClass?: string;
  withRouter?: boolean;
}>();
const emit = defineEmits<{
  (event: 'completed'): void;
}>();
const authorizationTheme = inject(
  INJECTION_KEYS.AuthorizationTheme,
  ThemeEnum.dark,
);

const dataLayer = useDataLayer();
const route = useRoute();
const router = useRouter();
const productCartStore = useProductCartStore();
const subscribeProductsStore = useSubscribeProductsStore();

const activeForm = useState<FormVariant>('active-auth-form', () =>
  typeof route.query.type === 'string' && route.query.type in FormVariant
    ? (route.query.type as FormVariant)
    : FormVariant.phone,
);
const phone = ref('');

const onPhoneFormSubmit = (value: { phone: string }) => {
  activeForm.value = FormVariant.phoneCode;
  phone.value = value.phone;
};

const onForgotPasswordFormSubmit = () => {
  activeForm.value = FormVariant.emailSent;
};

const onUserAuthorized = () =>
  setTimeout(() => {
    dataLayer.append({ event: 'auth' }, ['user']);
    request.hooks.unsubscribe('method:user:details:finish', onUserAuthorized);
  });

const onCompleted = () => {
  emit('completed');
  productCartStore.initCartData();
  subscribeProductsStore.fetchSubscribeProducts();
  request.hooks.subscribe('method:user:details:finish', onUserAuthorized);
};

watch(
  () => route.query.type,
  (v) => {
    if (v !== activeForm.value) activeForm.value = v as FormVariant;
  },
);

watch(activeForm, () => {
  if (props.withRouter) router.push({ query: { type: activeForm.value } });
});

onMounted(() => {
  if (!route.query.type) activeForm.value = FormVariant.phone;
});
</script>

<template>
  <div
    class="authorizationComponent"
    :class="`authorizationComponent_${authorizationTheme}`"
  >
    <div class="authorizationComponent__content">
      <h2
        v-if="title"
        class="authorizationComponent__title"
        :class="titleClass"
      >
        {{ title }}
      </h2>

      <Transition name="auth-page-content" mode="out-in">
        <AuthByPhoneForm
          v-if="activeForm === FormVariant.phone"
          @submit="onPhoneFormSubmit"
          @to-email="activeForm = FormVariant.email"
          :with-title="!title"
        />
        <AuthPhoneCodeForm
          v-else-if="activeForm === FormVariant.phoneCode"
          @change-phone="activeForm = FormVariant.phone"
          @to-email="activeForm = FormVariant.email"
          @completed="onCompleted"
          :phone="phone"
          :with-title="!title"
        />

        <AuthByEmailForm
          v-else-if="activeForm === FormVariant.email"
          @to-phone="activeForm = FormVariant.phone"
          @forgot-password="activeForm = FormVariant.forgotPassword"
          @completed="onCompleted"
          :with-title="!title"
        />
        <AuthForgotPasswordForm
          v-else-if="activeForm === FormVariant.forgotPassword"
          @submit="onForgotPasswordFormSubmit"
          @to-email="activeForm = FormVariant.email"
          :with-title="!title"
        />
        <AuthEmailSentForm
          v-else-if="activeForm === FormVariant.emailSent"
          @login="activeForm = FormVariant.email"
          :with-title="!title"
        />
      </Transition>
    </div>
  </div>
</template>

<style lang="scss" src="@/assets/styles/authorization.component.scss"></style>
